import React from "react";
import axios from "axios";
import { Marker, Popup, Tooltip } from "react-leaflet";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";

import { configAgencyName, defaultLanguage, fetchInterval, traccarApiKnotsToKmh, traccarCredentials } from "../config";
import { BusTooltip } from "./MapBusesComponent";
import { technicalVehicleIcon, personalCarIcon } from "../utils/mapIcons";
import variableColors from "../_App.module.scss";
import { removePKSXXX, timeFromNow, timeManipulation } from "../utils/helpers";
import offlineIcon from "../assets/icons/offlineIcon.svg";
import onlineIcon from "../assets/icons/onlineIcon.svg";
import unknownIcon from "../assets/icons/unknownIcon.svg";
import { AgencyContext } from "../hooks/AgencyContext";

const { darkColor, customSecondaryColor, indigoColor, customBgColor } = variableColors;

const BusTooltip2 = styled(BusTooltip)<{ $color: string }>`
  padding: 0.25rem 0.5rem;
  span:last-of-type {
    color: ${darkColor};
  }
  span:first-of-type {
    color: ${(props) => props.$color};
    border: 1px solid ${darkColor};
    padding: 1px 3px;
  }
`;

const MapTechnicalVehicles = ({ showLineNumbers }: { showLineNumbers: boolean }): JSX.Element => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth0();

  //* AgencyContext
  const { speedUnit } = React.useContext(AgencyContext) as AgencyContextType;

  const [selectedLanguage, setSelectedLanguage] = React.useState<string>(defaultLanguage);
  const [vehiclesDevices, setVehiclesDevices] = React.useState<VehicleDevice[]>([]);
  const [vehiclesPositions, setVehiclesPositions] = React.useState<VehiclePosition[]>([]);
  const [technicalVehicles, setTechnicalVehicles] = React.useState<TechnicalVehicle[]>([]);
  // console.log({ technicalVehicles }, new Date().toLocaleTimeString("pl-PL"));

  //* Change Language
  const i18nextLng = localStorage.getItem("i18nextLng");
  React.useEffect(() => {
    if (i18nextLng) {
      setSelectedLanguage(i18nextLng as string);
    }
  }, [i18nextLng]);

  //* Fetch all devices
  React.useEffect(() => {
    if (!traccarCredentials || !isAuthenticated) return;
    if (isAuthenticated && traccarCredentials && Object.keys(traccarCredentials)?.length) {
      const axiosConfigArray = (traccarCredentials as TraccarCredentials)?.credentials.map((auth) => {
        return {
          url: `${(traccarCredentials as unknown as TraccarCredentials)?.api as string}/devices` as string,
          method: "get",
          params: {},
          withCredentials: true,
          responseType: "json" as const,
          headers: { "content-type": "application/json", accept: "application/json" },
          auth: {
            username: auth.username as string,
            password: auth.password as string,
          },
        };
      });
      // console.log("axiosConfigArray:", axiosConfigArray);

      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      // console.log({ CancelToken, source });

      const setFetchInterval = async () => {
        await Promise.all(axiosConfigArray.map((elem) => axios(elem)))
          .then((data) => {
            // console.log("data:", data);
            let devicesArray = [] as VehicleDevice[];
            for (let i = 0; i < data.length; i++) {
              if (data[i].status === 200) {
                for (let j = 0; j < data[i].data.length; j++) {
                  data[i].data[j].email = data[i].config.auth?.username;
                }
                devicesArray.push(data[i].data);
              }
              devicesArray = devicesArray.flat(1);
              // console.log("devicesArray:", devicesArray);
              setVehiclesDevices(devicesArray);
            }
          })
          .catch((error) => {
            if (error.response) {
              console.error("error.response.status:", error.response.status);
            }
            console.error("error.config:", error.config);
          });
        // console.log("Devices were fetched", new Date().toLocaleTimeString("pl-PL"));
      };
      setFetchInterval();
      const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
      return () => {
        clearInterval(fetchingInterval);
        source.cancel();
        // console.log("Interval devices was cleared");
      };
    }
  }, [isAuthenticated]);

  //* Fetch all positions
  React.useEffect(() => {
    if (!traccarCredentials || !isAuthenticated) return;
    if (isAuthenticated && traccarCredentials && Object.keys(traccarCredentials).length) {
      const axiosConfigArray = (traccarCredentials as TraccarCredentials).credentials.map((auth) => {
        return {
          url: `${(traccarCredentials as unknown as TraccarCredentials).api}/positions` as string,
          method: "get",
          params: {},
          withCredentials: true,
          responseType: "json" as const,
          headers: { "content-type": "application/json", accept: "application/json" },
          auth: {
            username: auth.username as string,
            password: auth.password as string,
          },
        };
      });
      // console.log("axiosConfigArray:", axiosConfigArray);

      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      // console.log({ CancelToken, source });

      const setFetchInterval = async () => {
        await Promise.all(axiosConfigArray.map((elem) => axios(elem)))
          .then((data) => {
            // console.log("data:", data);
            let positionsArray = [] as VehiclePosition[];
            for (let i = 0; i < data.length; i++) {
              if (data[i].status === 200) {
                positionsArray.push(data[i].data);
              }
              positionsArray = positionsArray.flat(1);
              // console.log("positionsArray:", positionsArray);
              setVehiclesPositions(positionsArray);
            }
          })
          .catch((error) => {
            if (error.response) {
              console.error("error.response.status:", error.response.status);
            }
            console.error("error.config:", error.config);
          });
        // console.log("Positions were fetched", new Date().toLocaleTimeString("pl-PL"));
      };

      setFetchInterval();
      const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
      return () => {
        clearInterval(fetchingInterval);
        source.cancel();
        // console.log("Interval positions was cleared");
      };
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (configAgencyName && vehiclesPositions && vehiclesPositions.length && vehiclesDevices && vehiclesDevices.length) {
      let vehicleObjectsArray = [] as TechnicalVehicle[];

      for (let i = 0; i < vehiclesPositions.length; i++) {
        for (let j = 0; j < vehiclesDevices.length; j++) {
          if (
            vehiclesPositions[i].deviceId === vehiclesDevices[j].id ||
            vehiclesPositions[i].id === vehiclesDevices[j].positionId
          ) {
            const vehicleObject = Object.assign(vehiclesPositions[i], {
              lastUpdate: vehiclesDevices[j].lastUpdate,
              name: vehiclesDevices[j].name,
              status: vehiclesDevices[j].status,
              uniqueId: vehiclesDevices[j].uniqueId,
              disabled: vehiclesDevices[j].disabled,
              positionId: vehiclesDevices[j].positionId,
              email: vehiclesDevices[j].email,
            });
            vehicleObjectsArray.push(vehicleObject as TechnicalVehicle);
          }
        }
      }

      //* Remove pksxxx from buses names
      if (configAgencyName === ("PoznanPKS" as string)) {
        vehicleObjectsArray = removePKSXXX(vehicleObjectsArray, "name");
      }

      setTechnicalVehicles(vehicleObjectsArray);
    }
  }, [vehiclesDevices, vehiclesPositions]);

  const technicalVehiclesIcons: JSX.Element[] = technicalVehicles?.map((elem: TechnicalVehicle) => (
    <Marker
      key={elem.uniqueId}
      position={{
        lat: elem.latitude,
        lng: elem.longitude,
      }}
      icon={
        elem?.email?.includes("sluz") ? personalCarIcon : elem?.email?.includes("techn") ? technicalVehicleIcon : undefined
      }
    >
      {showLineNumbers && (
        <Tooltip
          permanent={true}
          className={
            elem?.email?.includes("sluz") ? "personal-car" : elem?.email?.includes("techn") ? "technical-vehicle" : undefined
          }
          direction={elem?.email?.includes("sluz") ? "left" : elem?.email?.includes("techn") ? "right" : undefined}
        >
          <BusTooltip2
            $color={
              elem?.email?.includes("sluz") ? indigoColor : elem?.email?.includes("techn") ? customSecondaryColor : darkColor
            }
          >
            <img
              width={16}
              height={16}
              src={elem?.status === "online" ? onlineIcon : elem?.status === "offline" ? offlineIcon : unknownIcon}
              alt="Online/Offline Icon"
            />
            <span>
              {elem?.email?.includes("sluz") ? t("Służbowy") : elem?.email?.includes("techn") ? t("Techniczny") : ""}
            </span>
            <span>{elem?.name || elem?.uniqueId || `${t("b/d")}`}</span>
          </BusTooltip2>
        </Tooltip>
      )}
      <Popup>
        {/* //* TracCar Api - sped unit: "mile/h" */}
        {t("Prędkość")}:{" "}
        <span className="span_bold">
          {speedUnit === "kmh"
            ? `${(elem?.speed * traccarApiKnotsToKmh)?.toFixed(1)} km/h`
            : speedUnit === "mph"
            ? `${elem?.speed?.toFixed(1)} mph`
            : t("b/d")}
        </span>
        <br />
        {t("Pojazd")} - {t("Nazwa")} <span className="span_bold">{elem?.name || `${t("b/d")}`}</span>
        <br />
        {t("Pojazd")} - {t("Id")} <span className="span_bold">{elem?.uniqueId || `${t("b/d")}`}</span>
        <br />
        {t("Pojazd")} - {t("Dodatkowe info")} <span className="span_bold">{elem?.pksName || `${t("b/d")}`}</span>
        <br />
        {t("Azymut")}: <span className="span_bold">{elem?.course ? `${elem?.course}\u00B0 ` : t("b/d")}</span>
        <br />
        {t("Protokół")}: <span className="span_bold"> {elem?.protocol || t("b/d")}</span>
        <br />
        {t("Id_urządzenia")}: <span className="span_bold"> {elem?.veh_dev_id || elem?.deviceId || t("b/d")}</span>
        <br />
        {t("Status")}:{" "}
        <span className="span_bold">
          {" "}
          {elem?.status || t("b/d")}
          <img
            style={{ border: `1px dotted ${darkColor}`, marginLeft: "0.5rem", backgroundColor: customBgColor }}
            width={16}
            height={16}
            src={elem?.status === "online" ? onlineIcon : elem?.status === "offline" ? offlineIcon : unknownIcon}
            alt="Online/Offline Icon"
          />
        </span>
        <br />
        {t("Pozycja")}:{" "}
        <span className="span_bold"> {`${elem?.latitude?.toFixed(4)}, ${elem?.longitude?.toFixed(4)}` || t("b/d")}</span>
        <br />
        {t("Wysokość")}: <span className="span_bold"> {elem?.altitude || t("b/d")}</span>
        <br />
        {t("Ostatnia aktualizacja")} <span className="span_bold"> {timeManipulation(elem?.lastUpdate) || t("b/d")}</span>
        <br />
        {t("Zaktualizowano")}:{" "}
        <span className="span_bold"> {timeFromNow(elem?.lastUpdate, selectedLanguage) || t("b/d")}</span>
      </Popup>
    </Marker>
  ));

  return <React.Fragment>{technicalVehiclesIcons}</React.Fragment>;
};

export default MapTechnicalVehicles;
